// @ts-check

import { html } from "lit-html";
import { copyTextToClipboard } from "../utils.js";

/** @typedef {ReturnType<typeof html>} LitView */
/** @typedef {import("./index.js").ToolEnv} ToolEnv */
/** @typedef {ReturnType<typeof createZeroWidthSpaceState>} ZeroWidthSpaceState */

const ICON_BUTTON_CONTENT_CLASS =
  "inline-flex min-w-0 items-center justify-center gap-1.5";

export const zwsTool = {
  key: "zws",
  path: "/zws",
  title: "Zero-Width Space",
  blurb: "Copy a zero-width space to the clipboard in one tap.",
  badge: "Utility",
  accentClass: "bg-gradient-to-r from-pink-300 via-rose-300 to-orange-300",
  createState: createZeroWidthSpaceState,
  render: renderZeroWidthSpacePage,
};

function createZeroWidthSpaceState() {
  return {
    copyStatus: "idle",
  };
}

/**
 * @param {ZeroWidthSpaceState} toolState
 * @param {ToolEnv} env
 * @returns {LitView}
 */
function renderZeroWidthSpacePage(toolState, env) {
  const buttonLabel = getButtonLabel(toolState.copyStatus);
  const buttonClass = getButtonClass(toolState.copyStatus);
  const message = getMessage(toolState.copyStatus);

  return html`
    <div class="flex flex-col items-center gap-6 py-16 text-center">
      <p class="max-w-md text-sm leading-6 text-stone-500">
        One tap copies <code>\u200B</code> to the clipboard. Useful where an invisible separator is needed.
      </p>
      <button
        class=${buttonClass}
        type="button"
        @click=${async function () {
          try {
            await copyTextToClipboard("\u200B");
            env.setTemporaryStatus(toolState, "copyStatus", "copied", "idle", "zws");
          } catch (error) {
            console.error("[app] zero-width space copy failed", error);
            env.setTemporaryStatus(toolState, "copyStatus", "error", "idle", "zws");
          }
        }}
      >
        <span class=${ICON_BUTTON_CONTENT_CLASS}>
          ${renderCopyIcon()}
          <span>${buttonLabel}</span>
        </span>
      </button>
      ${message ? html`<p class="text-sm text-stone-500">${message}</p>` : ""}
    </div>
  `;
}

/**
 * @returns {LitView}
 */
function renderCopyIcon() {
  return html`
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6 shrink-0" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
    </svg>
  `;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getButtonClass(copyStatus) {
  let result = "inline-flex h-12 items-center justify-center rounded-lg bg-stone-900 px-8 text-base font-semibold text-white transition hover:bg-stone-800";
  if (copyStatus === "copied") {
    result = "inline-flex h-12 items-center justify-center rounded-lg bg-emerald-700 px-8 text-base font-semibold text-white";
  }

  if (copyStatus === "error") {
    result = "inline-flex h-12 items-center justify-center rounded-lg bg-red-600 px-8 text-base font-semibold text-white";
  }

  return result;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getButtonLabel(copyStatus) {
  let result = "Copy zero-width space";
  if (copyStatus === "copied") {
    result = "Copied";
  }

  if (copyStatus === "error") {
    result = "Copy failed";
  }

  return result;
}

/**
 * @param {string} copyStatus
 * @returns {string}
 */
function getMessage(copyStatus) {
  let result = "";
  if (copyStatus === "copied") {
    result = "The invisible character is now on your clipboard.";
  }

  if (copyStatus === "error") {
    result = "Clipboard access failed. Try again or allow clipboard permissions.";
  }

  return result;
}
